<template>
    <div>
        <loader v-if="!setupIntent" />
        <div class="radio-container" v-if="setupIntent">
            <radio-field-new class="radio-field-new" v-for="payment in payments" v-show="payment.key !== 'wallet' || (payment.key === 'wallet' && loadWallet)" :key="payment.key" :item="payment" v-model="selectedRadio" :selected="selectedRadio" @clicked="toggleSelected">
                <component :is="payment.component" v-bind="additionalProperties(payment)" @cardUpdated="cardUpdated" @save-card-token="saveCardTokenForGifting" @closed="close"/>
            </radio-field-new>
        </div>

        <p class="error my-2" v-text="error" v-if="error"></p>
        <div class="w-full flex">
            <div class="w-full md:w-1/2 p-2">
                <button
                    v-if="selectedRadio === 'card'"
                    class="btn btn--primary btn--full-width btn--auto-width"
                    :disabled="processing"
                    @click="updateCustomersCard">
                    Update
                </button>
            </div>
            <div class="w-full md:w-1/2 p-2">
                <button class="btn btn--secondary btn--full-width btn--auto-width"
                    v-if="selectedRadio === 'card'"
                    :disabled="processing"
                    @click="close">
                    Cancel
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import PaymentService from '@/services/payments/PaymentService';
import RadioFieldNew from '../fields/RadioFieldNew';
import Loader from '@/components/structure/Loader';
import CountryService from '@/services/logistics/CountryService';

const paymentsForm = require('@/helpers/PaymentHelpers').payments.filter(item => item.enabled);

let paymentService = new PaymentService();

export default {
    name: 'EditCardDetailsFormNew',
    components: {
        RadioFieldNew,
        Loader,
    },
    data() {
        return {
            error: null,
            cardToken: null,
            cardFullName: null,
            processing: false,
            payments: null,
            stripe: null,
            elements: null,
            prButton: null,
            paymentRequest: null,
            loadWallet: false,
            selectedRadio: paymentsForm && paymentsForm.filter(item => item.isSelected === true)[0]?.key,
        };
    },
    props: {
        isUpdate: {
            default: false,
            type: Boolean,
        },
        isGifting: {
            default: false,
            type: Boolean,
        },
    },
    mounted() {
        this.fetchStripeSetupIntent({ email: this.email, gateway: this.paymentGateway, payment_method: 'card' });
    },
    created() {
        this.cardFullName = this.fullName;
        this.canMakePayment();
    },
    methods: {
        ...mapActions({
            fetchStripeSetupIntent: 'account/fetchStripeSetupIntent',
        }),
        close() {
            this.$emit('closed');
        },
        toggleProcessing() {
            this.processing = !this.processing;
        },
        handleFail() {
            this.processing = false;
            this.$scrollTo(`.input--invalid`, {
                offset: -100,
            });
        },

        canMakePayment() {
            this.stripe = Stripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY);
            let country = CountryService.findCountryByIso(process.env.VUE_APP_COUNTRY_ISO);
            this.paymentRequest = this.stripe.paymentRequest({
                country: country.iso2,
                currency: (process.env.VUE_APP_CURRENCY).toLowerCase(),
                requestPayerName: true,
                requestPayerEmail: true,
                total: {
                    label: 'Order Total',
                    amount: Number(process.env.VUE_APP_BOX_PRICE) * 100 || 0,
                },
            });

            this.elements = this.stripe.elements();
            this.prButton = this.elements.create('paymentRequestButton', {
                paymentRequest: this.paymentRequest,
            });

            try {
                this.paymentRequest.canMakePayment()
                    .then((result) => {
                        if (result) {
                            this.loadWallet = true;
                            this.prButton.mount('#payment-request-button');
                        } else {
                            this.loadWallet = false;
                        }
                    });
            } catch (err) {
                console.log(err);
            } finally {
                this.payments = paymentsForm;
            }
        },

        saveCardTokenForGifting(paymentToken) {
            this.$emit('new-card-token', paymentToken);
            this.isGifting && this.$emit('same-card-toggle');
        },

        updateCustomersCard() {
            // Send via API using service
            this.error = null;

            this.toggleProcessing();

            this.$refs.cardForm.handleScaCardSubmission()
                .then(tokenData => {
                    if (this.isGifting) {
                        this.$emit('new-card-token', tokenData.setupIntentId);
                        this.isGifting && this.$emit('same-card-toggle');
                    } else {
                        paymentService.updatePaymentSource(this.userId, tokenData.setupIntentId, tokenData.setupIntentId, this.cardFullName)
                            .then(() => {
                                this.$store.dispatch('account/fetchUserData', true);
                                this.close();
                            })
                            .catch(error => {
                                this.toggleProcessing();
                                this.$store.commit('account/setSetupIntent', null);
                                this.fetchStripeSetupIntent({ email: this.email, gateway: 'Stripe' });
                                const hasAmex = error.response?.data?.error.includes('Amex');
                                if (hasAmex) return this.error = this.$t('AMERICAN_EXPRESS_NOT_SUPPORTED');
                                this.error = error.response.data.error;
                            });
                    }
                })
                .catch((err) => {
                    this.error = err;
                    this.handleFail();
                });
        },

        additionalProperties(component) {
            if (component.key === 'wallet') {
                return {
                    setupIntent: this.setupIntent,
                    isUpdate: this.isUpdate,
                    isGifting: this.isGifting,
                };
            };
            if (component.key === 'card') {
                return {
                    ref: 'cardForm',
                    fullName: this.fullName,
                    billingAddress: this.billingAddress,
                    hasBeenRequested: this.userHasRequesed,
                };
            };
        },

        toggleSelected(id) {
            this.selectedRadio = id;
            return this.payments.map(item => {
                if (item.key === id) {
                    item.isSelected = true;
                } else {
                    item.isSelected = false;
                }
            });
        },

        cardUpdated() {
            this.$store.dispatch('account/fetchUserData', true);
            this.close();
        },
    },
    computed: {
        ...mapGetters({
            billingAddress: 'payment/getBillingAddress',
            userId: 'account/getUserId',
            email: 'account/getEmail',
            fullName: 'account/getFullName',
            setupIntent: 'account/getSetupIntent',
            paymentGateway: 'payment/getStripeGateway',
        }),
    },
    watch: {
        cardToken: {
            handler: function(cardToken) {
                if (cardToken && cardToken.id) this.updateCustomersCard();
            },
            deep: true,
        },
    },
};
</script>

<style scoped>
    .radio-container{
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 20px auto 20px;
        flex-direction: column;
        max-width: 400px;
    }
    .radio-field-new {
        width: 100%;
    }
</style>
